import { useEffect, useState } from "react";
import { get, onValue, push, ref, remove, serverTimestamp, set, update } from "firebase/database";
import { auth, rtdb } from "../firebase";
import { roomIdle, serverNow } from "./room";
import { banActive } from "./banTime";
import { blockedHere } from "./browserBlock";

// The site owner. Only this account (with its email verified) can open the
// moderation page, and the database rules check the same thing
// (database.rules.json), so the page can't be faked from a browser.
export const ADMIN_EMAIL = "yahia6taha6@gmail.com";
export const isAdmin = (user) => !!(user && user.emailVerified && (user.email || "").toLowerCase() === ADMIN_EMAIL);

// A random code this browser keeps, even after signing out. Bans cover it too,
// so a banned player can't just sign out and come back as a guest.
const DEVICE_KEY = "partycade.device";
let device = null;
const newDeviceCode = () => {
  try {
    return Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => b.toString(36).padStart(2, "0")).join("");
  } catch {
    return (Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)).slice(0, 20).padEnd(16, "0");
  }
};
export function deviceId() {
  if (device) return device;
  try {
    device = localStorage.getItem(DEVICE_KEY);
  } catch {}
  if (!device || !/^[a-z0-9]{16,40}$/.test(device)) {
    device = newDeviceCode();
    try {
      localStorage.setItem(DEVICE_KEY, device);
    } catch {}
  }
  return device;
}

// ---- Bans ------------------------------------------------------------------
// bans/users/{uid} and bans/devices/{device}: { at, name, reason }.

// Whether this player (or this browser) is banned, and until when:
// { banned, until, reason }. `banned` is null while checking; `until` is a
// timestamp (ms) for a temporary ban, or null if it lasts until the owner lifts
// it. A temporary ban ends by itself: this re-renders the moment it does.
export function useBan(uid) {
  const [byUser, setByUser] = useState(null); // the ban entry, false for none
  const [byDevice, setByDevice] = useState(null);
  const [, tick] = useState(0);
  useEffect(() => {
    if (!uid) return setByUser(false);
    return onValue(ref(rtdb, `bans/users/${uid}`), (s) => setByUser(s.exists() ? s.val() : false), () => setByUser(false));
  }, [uid]);
  useEffect(
    () => onValue(ref(rtdb, `bans/devices/${deviceId()}`), (s) => setByDevice(s.exists() ? s.val() : false), () => setByDevice(false)),
    []
  );

  const now = serverNow();
  const active = [byUser, byDevice].filter((e) => e && banActive(e, now));
  // Wake up when the first of them runs out.
  const soonest = active.length && active.every((e) => e.until) ? Math.min(...active.map((e) => e.until)) : null;
  useEffect(() => {
    if (!soonest) return;
    const id = setTimeout(() => tick((n) => n + 1), Math.min(Math.max(0, soonest - serverNow()) + 300, 2 ** 31 - 1));
    return () => clearTimeout(id);
  }, [soonest]);

  // Banned automatically for writing a swastika (lib/strikes.js) on this browser.
  if (blockedHere(uid, byUser ? banActive(byUser, now) : byUser)) return { banned: true, until: null, reason: "" };
  if (byUser === null || byDevice === null) return { banned: null, until: null, reason: "" };
  if (!active.length) return { banned: false, until: null, reason: "" };
  const forever = active.some((e) => !e.until);
  return {
    banned: true,
    until: forever ? null : Math.max(...active.map((e) => e.until)),
    reason: active.map((e) => e.reason).find(Boolean) || "",
  };
}

// Just whether (null while checking).
export const useBanned = (uid) => useBan(uid).banned;

// `until`: when the ban ends (ms, server time), or leave it out for a ban that
// lasts until the owner lifts it. Every ban is also written to the ban history
// (banHistory/{id}), which keeps it after it ends or is lifted. The ban holds
// the id of its history entry as `hid`.
export async function banPlayer({ uid, device: dev, name, reason = "", until = null }) {
  // The owner can't ban themselves, or the browser they're using right now
  // (a friend who played on the owner's computer only loses their account).
  if (uid === auth.currentUser?.uid) throw new Error("You can't ban yourself.");
  if (dev === deviceId()) dev = null;
  const base = {
    at: serverTimestamp(),
    name: (name || "?").slice(0, 40),
    reason: reason.slice(0, 200),
    ...(until ? { until: Math.round(until) } : {}),
    ...(dev ? { device: dev } : {}),
  };
  const hid = push(ref(rtdb, "banHistory")).key;
  const entry = { ...base, hid };
  const patch = { [`bans/users/${uid}`]: entry, [`banHistory/${hid}`]: { uid, ...base } };
  if (dev) patch[`bans/devices/${dev}`] = { ...entry, uid };
  // A ban that this one replaces is marked as replaced in the history (one that
  // had already run out stays "expired").
  const old = (await get(ref(rtdb, `bans/users/${uid}`))).val();
  if (old) Object.assign(patch, await closeInHistory(uid, old, banActive(old, serverNow()) ? "replacedAt" : null));
  await update(ref(rtdb), patch);
}

// The history writes that end `entry` (a ban from bans/users/{uid}): its own
// history entry gets `field` (liftedAt or replacedAt, or nothing for null), or,
// for a ban that was never logged (automatic ones, and old ones), a new entry
// is made from it.
async function closeInHistory(uid, entry, field) {
  const { hid, ...rest } = entry;
  const mark = field ? { [field]: serverTimestamp() } : {};
  if (hid && (await get(ref(rtdb, `banHistory/${hid}`))).exists()) return field ? { [`banHistory/${hid}/${field}`]: serverTimestamp() } : {};
  // Old entries might lack a field the history requires.
  const log = { uid, ...rest, ...mark, at: typeof rest.at === "number" ? rest.at : 0, name: String(rest.name || "?").slice(0, 40) };
  return { [`banHistory/${push(ref(rtdb, "banHistory")).key}`]: log };
}

// Lifting a ban also clears the player's strikes (lib/strikes.js), so they
// start over. The ban stays in the history, marked as lifted.
export async function unban(uid) {
  const snap = await get(ref(rtdb, `bans/users/${uid}`));
  const entry = snap.val();
  const dev = entry?.device;
  const patch = { [`bans/users/${uid}`]: null, [`strikes/${uid}`]: null, ...(dev ? { [`bans/devices/${dev}`]: null } : {}) };
  if (entry) Object.assign(patch, await closeInHistory(uid, entry, "liftedAt"));
  await update(ref(rtdb), patch);
}

export const useBans = () => useLive("bans/users");
// Every ban ever made, and how it ended: { id: { uid, name, reason, at, until?, liftedAt?, replacedAt? } }.
export const useBanHistory = () => useLive("banHistory");
// Strikes for writing a swastika in chat: { uid: { n: { at, room } } }.
export const useStrikes = () => useLive("strikes");

// ---- Rooms -----------------------------------------------------------------

// Every room made today or yesterday (roomIndex), with its meta and players.
export async function listRooms() {
  const index = (await get(ref(rtdb, "roomIndex"))).val() || {};
  const today = Math.floor(Date.now() / 86400000);
  const codes = Object.entries(index)
    .filter(([, day]) => day >= today - 1)
    .map(([code]) => code);
  const rooms = await Promise.all(
    codes.map(async (code) => {
      const [meta, players] = await Promise.all([get(ref(rtdb, `rooms/${code}/meta`)), get(ref(rtdb, `rooms/${code}/players`))]);
      return meta.exists() ? { code, meta: meta.val(), players: players.val() || {} } : null;
    })
  );
  return rooms.filter(Boolean).sort((a, b) => (b.meta.createdAt || 0) - (a.meta.createdAt || 0));
}

// Removes a player from a room and keeps them out of it.
export const kickFromRoom = (code, uid) =>
  update(ref(rtdb, `rooms/${code}`), { [`players/${uid}`]: null, [`meta/kicked/${uid}`]: true });

export const closeRoom = (code) => Promise.all([remove(ref(rtdb, `rooms/${code}`)), remove(ref(rtdb, `roomIndex/${code}`))]);

// Closes every room nobody is in and nobody has used for a while (see
// "Tidying up" in room.js). Runs from the owner's browser, which can see every
// room. Returns how many it closed.
export async function closeIdleRooms() {
  const index = (await get(ref(rtdb, "roomIndex"))).val() || {};
  let closed = 0;
  for (const code of Object.keys(index)) {
    const [meta, players] = await Promise.all([get(ref(rtdb, `rooms/${code}/meta`)), get(ref(rtdb, `rooms/${code}/players`))]);
    const online = Object.values(players.val() || {}).some((p) => p.online);
    if (!meta.exists() || (!online && roomIdle(meta.val()))) {
      await closeRoom(code);
      if (meta.exists()) closed++;
    }
  }
  return closed;
}

// ---- Casino access ---------------------------------------------------------
// casino/requests/{uid}: { name, email, birthday, adult, at }, written by the
// player (accounts only). casino/approved/{uid}: { name, at }, written by the
// owner, who answers within REVIEW_HOURS.
export const CASINO_GAMES = ["blackjack", "roulette", "poker"];
export const isCasino = (game) => CASINO_GAMES.includes(game);
export const REVIEW_HOURS = 24;
export const MIN_AGE = 18;

// Whole years old on `today` for a "YYYY-MM-DD" birthday, or null if it isn't
// a real date (or is in the future).
export function ageOn(birthday, today = new Date()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthday || "");
  if (!m) return null;
  const [y, mo, d] = [+m[1], +m[2], +m[3]];
  const date = new Date(Date.UTC(y, mo - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== mo - 1 || date.getUTCDate() !== d) return null;
  const [ty, tm, td] = [today.getFullYear(), today.getMonth() + 1, today.getDate()];
  const age = ty - y - (tm < mo || (tm === mo && td < d) ? 1 : 0);
  return age < 0 ? null : age;
}

// { approved, request }: approved is null while checking; request is the
// player's open request (undefined while checking, null if there's none).
export function useCasinoAccess(uid) {
  const [state, setState] = useState({ approved: null, request: undefined });
  useEffect(() => {
    if (!uid) return setState({ approved: false, request: null });
    setState({ approved: null, request: undefined });
    const set = (patch) => setState((x) => ({ ...x, ...patch }));
    const offA = onValue(ref(rtdb, `casino/approved/${uid}`), (s) => set({ approved: s.exists() }), () => set({ approved: false }));
    const offR = onValue(ref(rtdb, `casino/requests/${uid}`), (s) => set({ request: s.val() }), () => set({ request: null }));
    return () => {
      offA();
      offR();
    };
  }, [uid]);
  return state;
}

// The player ticks "I'm 18 or older" and gives their birthday to ask.
export const requestCasino = ({ uid, name, email, birthday }) =>
  set(ref(rtdb, `casino/requests/${uid}`), {
    name: (name || "?").slice(0, 40),
    email: (email || "").slice(0, 120),
    birthday,
    adult: true,
    at: serverTimestamp(),
  });

export const approveCasino = (uid, name) =>
  update(ref(rtdb), { [`casino/approved/${uid}`]: { name: name || "?", at: serverTimestamp() }, [`casino/requests/${uid}`]: null });
export const denyCasino = (uid) => remove(ref(rtdb, `casino/requests/${uid}`));
export const cancelCasinoRequest = (uid) => remove(ref(rtdb, `casino/requests/${uid}`));
export const revokeCasino = (uid) => remove(ref(rtdb, `casino/approved/${uid}`));

export const useCasinoRequests = () => useLive("casino/requests");
export const useCasinoApproved = () => useLive("casino/approved");

// Messages from the contact form (lib/contact.js). Deleting one marks it dealt with.
export const useContactMessages = () => useLive("contact");
export const deleteContact = (id) => remove(ref(rtdb, `contact/${id}`));

function useLive(path) {
  const [value, setValue] = useState(null);
  useEffect(() => onValue(ref(rtdb, path), (s) => setValue(s.val() || {}), () => setValue({})), [path]);
  return value;
}
